import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import clsx from 'clsx'

import { spacing, typography } from '~tokens/internal'

import Button from '../Button'
import Icon from '../Icon'
import TextField from '../TextField'
import Typography from '../Typography'

import './Header.scss'

export interface HeaderHandle {
  startEditing: () => void
}

export interface HeaderProps {
  title: string
  variant?: 'default' | 'hoverEdit'
  description?: string
  subHeading?: React.ReactNode
  headerIcon?: React.ReactNode
  headerTitleAddon?: React.ReactNode
  primaryButton?: React.ReactNode
  secondaryButton?: React.ReactNode
  onTitleChange?: (title: string) => void
  titleReadonly?: boolean
  truncateTitle?: boolean
}
const Header = forwardRef<HeaderHandle, HeaderProps>(
  (
    {
      title,
      variant = 'default',
      description,
      headerIcon,
      headerTitleAddon,
      subHeading,
      primaryButton,
      secondaryButton,
      onTitleChange,
      titleReadonly,
      truncateTitle,
    },
    ref,
  ) => {
    const [editing, setEditing] = useState(false)
    const [editValue, setEditValue] = useState('')
    const cancelledRef = useRef(false)

    const isEditable = Boolean(onTitleChange) && !titleReadonly

    useImperativeHandle(
      ref,
      () => ({
        startEditing: (): void => {
          if (isEditable && !editing) {
            setTimeout(() => {
              setEditValue(title)
              setEditing(true)
            }, 0)
          }
        },
      }),
      [isEditable, editing, title],
    )

    const startEdit = (): void => {
      setEditValue(title)
      setEditing(true)
    }

    const confirmEdit = (): void => {
      onTitleChange?.(editValue)
      setEditing(false)
    }

    const cancelEdit = (): void => {
      cancelledRef.current = true
      setEditing(false)
    }

    const renderTitle = (): React.ReactElement => {
      if (isEditable && editing) {
        return (
          <Box className='header-title'>
            <div className='header__title'>
              <Box className='header__edit-row'>
                <TextField
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                  className='header__edit-field'
                  sx={(theme) => ({
                    margin: `calc(-1 * ${spacing.xxs}) calc(-1 * ${spacing.compact})`,
                    '& .MuiInputBase-input': {
                      ...theme.typography.h3,
                      height: 'auto',
                      width: `${Math.max(editValue.length + 2, 12)}ch`,
                      maxWidth: '100%',
                    },
                  })}
                  containerStyle={{ width: 'fit-content', maxWidth: '100%', minWidth: 0 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      cancelledRef.current = true
                      confirmEdit()
                    }
                    if (e.key === 'Escape') {
                      cancelEdit()
                    }
                  }}
                  onBlur={() => {
                    if (variant === 'hoverEdit') {
                      if (!cancelledRef.current) {
                        if (editValue !== title) {
                          confirmEdit()
                        } else {
                          cancelEdit()
                        }
                      }

                      cancelledRef.current = false
                    }
                  }}
                />
                {variant === 'hoverEdit' ? (
                  headerTitleAddon && (
                    <span className='header-title__addon'>{headerTitleAddon}</span>
                  )
                ) : (
                  <>
                    <Button variant='text' onClick={confirmEdit} className='header__edit-btn'>
                      <Icon name='Check' size={typography.fontSizePx.sm} />
                    </Button>
                    <Button variant='text' onClick={cancelEdit} className='header__edit-btn'>
                      <Icon name='Close' size={typography.fontSizePx.sm} />
                    </Button>
                  </>
                )}
              </Box>
            </div>
          </Box>
        )
      }

      return (
        <Box className='header-title'>
          <div
            className={clsx('header__title', !isEditable && 'header__title--static')}
            onClick={isEditable ? startEdit : undefined}
          >
            {truncateTitle ? (
              <Tooltip title={title} arrow placement='top'>
                <Typography
                  component='h1'
                  variant='h3'
                  color='textPrimary'
                  noWrap
                  className={clsx(
                    isEditable && variant === 'hoverEdit' && 'header__title--editable',
                    'header__title--truncated',
                  )}
                >
                  {title}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                component='h1'
                variant='h3'
                color='textPrimary'
                className={
                  isEditable && variant === 'hoverEdit' ? 'header__title--editable' : undefined
                }
              >
                {title}
              </Typography>
            )}
            {headerTitleAddon && (
              <span className='header-title__addon' onClick={(e) => e.stopPropagation()}>
                {headerTitleAddon}
              </span>
            )}
            {isEditable && variant !== 'hoverEdit' && (
              <span className='header__edit-icon'>
                <Icon name='Edit' size={typography.fontSizePx.xs} />
              </span>
            )}
          </div>
        </Box>
      )
    }

    return (
      <Box className='header-container'>
        <Box className='header-icon-container'>
          {headerIcon}
          <Box className='header-content'>
            {renderTitle()}
            {subHeading}
            {description && (
              <Typography
                sx={{
                  color: 'text.secondary',
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className='button-container'>
          {secondaryButton}
          {primaryButton}
        </Box>
      </Box>
    )
  },
)

Header.displayName = 'Header'

export default Header
